/* ==============================================================================
   Barra de comando — UMA barra só, flutuando na base
   ==============================================================================
   O que mudou estruturalmente, e por que era o problema central
   =============================================================
   Antes havia DOIS componentes com naturezas diferentes disputando a mesma
   região da tela: a `Dock` (fileira de servidores, flutuando embaixo) e a
   `VoiceBar` (controles de chamada, encostada no rodapé do painel principal).

   Duas barras na base significam duas alturas, dois raios, dois tratamentos de
   sombra e duas gramáticas de botão — e o olho lê isso como interface montada
   por partes. Era o "menus confusos": o microfone ficava num retângulo dentro do
   painel e o servidor num círculo fora dele, a 8px de distância.

   Aqui é uma cápsula única, e a leitura é da esquerda para a direita:

     [servidores]  │  [microfone câmera tela silenciar]  [ENCERRAR]  [config]  │  [você]
        navegação        o que está acontecendo agora                            identidade

   As duas barras verticais não são adorno: separam três CLASSES de ação que não
   se confundem. Navegar entre servidores não tem nada a ver com desligar o
   microfone, e as duas coisas não podem morar na mesma fileira sem um respiro
   que diga isso.

   Rótulo de texto embaixo de cada ícone de chamada
   ================================================
   Não é redundância com o `title`. "Compartilhar tela" e "câmera" são ícones
   parecidos em qualquer conjunto — dois retângulos — e num app de voz o custo de
   errar é alto: compartilhar a tela sem querer expõe o que estava aberto. O
   rótulo permanente elimina a hesitação, e é o que a referência mostra.

   Os controles de servidor NÃO têm rótulo, de propósito: o ícone do servidor JÁ
   é o rótulo dele, e uma legenda embaixo de cinco círculos duplicaria a
   informação e dobraria a altura da barra.

   Quando não há chamada
   =====================
   Os controles e a pílula vermelha ENCOLHEM até zero em vez de desaparecerem de
   golpe, e a cápsula acompanha. Sumir de golpe faz a barra dar um pulo lateral
   — e a barra é o elemento fixo da tela, o único que nunca deveria saltar.

   A técnica é `grid-template-columns: 0fr -> 1fr`, que é a forma de animar
   largura de conteúdo intrínseco em CSS puro. `width: auto` não interpola.
   ============================================================================== */

import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Avatar, type AvatarMedia, type AvatarStatus } from '../components/Avatar.tsx';
import { GuildIcon } from '../components/GuildIcon.tsx';

/* ==============================================================================
   Modelo
============================================================================== */

export interface CommandBarGuild {
  id: string;
  name: string;
  /** URL do ícone. Ausente cai no fallback com iniciais. */
  iconUrl?: string | null;
  /** Há mensagem não lida neste servidor. */
  unread?: boolean;
  /** Quantidade de menções diretas. Mostra contador. */
  mentions?: number;
}

/**
 * Estado da chamada em curso.
 *
 * Objeto único e não uma dúzia de props soltas: `voice` ausente significa "não
 * há chamada", e é essa ausência que faz a cápsula encolher. Com props soltas a
 * condição seria uma conjunção de sete testes, e cada chamador escreveria a sua.
 */
export interface CommandBarVoice {
  channelName: string;
  participantCount: number;

  micEnabled: boolean;
  /** 0 a 1, pós-processamento. Modula o brilho do botão de microfone. */
  micLevel?: number;
  onToggleMic: () => void;

  cameraEnabled: boolean;
  onToggleCamera: () => void;

  /** Quantas telas ESTE usuário está compartilhando. */
  screenCount: number;
  /** Teto configurado. Acima dele o botão fica indisponível. */
  maxScreens: number;
  onShareScreen: () => void;

  deafened: boolean;
  onToggleDeafen: () => void;

  /**
   * Abre o seletor de dispositivos. Coordenadas de viewport, e QUAL seção.
   *
   * `section` existe porque há DUAS setas — uma no microfone e uma no fone — e
   * cada uma tem de abrir o painel na parte que ela promete. Uma seta só, entre
   * os dois botões, foi a primeira tentativa: a ideia era que ela pertencesse
   * aos dois, e o resultado é que ela lê como pertencendo ao da esquerda. O
   * relato foi direto: "você esqueceu da seleção do fone também, só faz do mic".
   *
   * Ausente esconde as setas — o certo quando o consumidor ainda não tem para
   * onde levar o clique, em vez de uma seta que não faz nada.
   */
  onOpenDevices?:
    | ((x: number, y: number, section: 'input' | 'output') => void)
    | undefined;

  onLeave: () => void;

  /** Upload medido e orçamento, em kbps. Alimenta o chip de banda. */
  uploadKbps?: number;
  budgetKbps?: number;
  /** Aviso do governador de banda, quando houver. */
  notice?: string | null;
  onOpenBandwidth?: (() => void) | undefined;
}

/** Chip de identidade na ponta direita da cápsula. */
export interface CommandBarUser {
  id: string;
  displayName: string;
  username?: string;
  status?: AvatarStatus;
  avatar?: AvatarMedia | null;
  /** Texto do estado. Ausente cai no rótulo do `status`. */
  presenceText?: string;
}

export interface CommandBarProps {
  guilds: ReadonlyArray<CommandBarGuild>;
  activeGuildId: string | null;
  onSelectGuild: (id: string) => void;
  onCreateGuild: () => void;
  onOpenSettings: () => void;

  /**
   * Destino "Início": amigos e conversas diretas.
   *
   * Fica na PONTA ESQUERDA, antes dos servidores, separado por um fio. Não é um
   * servidor — é o lugar das conversas que não pertencem a servidor nenhum, e
   * misturá-lo na fileira faria a pessoa procurá-lo entre os ícones.
   */
  onOpenHome?: (() => void) | undefined;
  homeActive?: boolean | undefined;
  /** Não lidas nas conversas diretas. */
  homeMentions?: number | undefined;
  /** Slot extra antes das configurações, para ações futuras. */
  trailing?: ReactNode;

  /**
   * Chamada em curso. Ausente ou nulo = cápsula curta, sem controles.
   *
   * Opcional para o consumidor que ainda passa a barra de voz separada
   * continuar compilando sem alteração.
   */
  voice?: CommandBarVoice | null | undefined;

  /** Chip do usuário. Ausente = a cápsula termina nas configurações. */
  user?: CommandBarUser | null | undefined;
  /** Clique no chip (nome/avatar). */
  onOpenProfile?: (() => void) | undefined;
  /** Clique na engrenagem DO CHIP — preferências da conta, não do app. */
  onOpenAccountSettings?: (() => void) | undefined;
}

/* ==============================================================================
   Seta de dispositivos
   ==============================================================================
   Estreita de propósito: 16px de largura contra os 52 de um botão de chamada.
   Ela não é uma ação de mesmo peso — é o "detalhe" do microfone e do fone, e um
   alvo do mesmo tamanho competiria com eles pelo clique.

   Há DUAS: uma logo depois do microfone, outra logo depois do fone. A primeira
   versão tinha uma só, entre os dois botões, com o argumento de que assim ela
   pertenceria a ambos. Na prática uma seta lê como pertencendo ao controle à
   sua ESQUERDA — é assim em todo menu suspenso — e a seleção de saída ficou
   invisível.

   Cada seta abre o mesmo painel, na seção que ela promete.
============================================================================== */

function DeviceChevron({
  label,
  section,
  onOpen,
}: {
  label: string;
  section: 'input' | 'output';
  onOpen: (x: number, y: number, section: 'input' | 'output') => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(event) => {
        /*
         * A âncora sai do BOTÃO, não do ponteiro.
         *
         * `event.clientX` abriria o painel onde o dedo tocou, que num alvo de
         * 16px varia o suficiente para o painel dançar entre cliques. E pelo
         * teclado não haveria coordenada nenhuma — `clientX` é 0, e o painel
         * abriria no canto da tela.
         */
        const r = event.currentTarget.getBoundingClientRect();
        onOpen(r.left + r.width / 2, r.top - 8, section);
      }}
      className="grid shrink-0 place-items-center transition-colors"
      style={{
        width: 16,
        height: 52,
        borderRadius: 'var(--radius-sm)',
        color: 'var(--color-text-tertiary)',
      }}
      onMouseEnter={(event) => {
        event.currentTarget.style.background = 'oklch(100% 0 0 / 0.07)';
        event.currentTarget.style.color = 'var(--color-text-primary)';
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.background = 'transparent';
        event.currentTarget.style.color = 'var(--color-text-tertiary)';
      }}
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"
           aria-hidden="true">
        <path d="M6 15l6-6 6 6" />
      </svg>
    </button>
  );
}

/* ==============================================================================
   Geometria
   ==============================================================================
   Os números não são um "sistema de 8": eles vêm de restrições diferentes e é
   por isso que não são múltiplos uns dos outros.

     44  círculo de servidor. Alvo de toque mínimo recomendado, e o menor
         diâmetro em que duas iniciais ainda caem legíveis.
     52  altura do botão de chamada com rótulo. 20 de ícone + 4 de respiro +
         11 de linha de texto, arredondado.
     40  altura do chip do usuário. Menor que o círculo de servidor de
         propósito: ele é identidade, não ação frequente.
============================================================================== */
const GUILD_SIZE = 44;
/** Quanto o ícone sob o cursor cresce. */
const MAX_SCALE = 0.28;
/** Alcance da onda, em número de itens. */
const FALLOFF = 1.5;

/**
 * Peso da magnificação pela distância ao cursor.
 *
 * Curva gaussiana e não linear: o decaimento linear produz um vinco visível no
 * limite do alcance, porque a derivada muda de golpe. A gaussiana chega a zero
 * suavemente, que é o que faz a fileira parecer uma superfície contínua.
 *
 * A amplitude caiu de 0.5 para 0.28 em relação à dock antiga. Com a barra
 * unificada a fileira de servidores é uma das três zonas da cápsula, não a
 * cápsula inteira — um ícone crescendo 50% ali competia com a pílula vermelha.
 */
function magnification(distance: number): number {
  return Math.exp(-((distance / FALLOFF) ** 2));
}

const STATUS_LABEL: Record<AvatarStatus, string> = {
  online: 'Online',
  idle: 'Ausente',
  dnd: 'Não perturbe',
  offline: 'Offline',
};

/* ==============================================================================
   Componente
============================================================================== */

export function CommandBar({
  guilds,
  activeGuildId,
  onSelectGuild,
  onCreateGuild,
  onOpenSettings,
  onOpenHome,
  homeActive = false,
  homeMentions,
  trailing,
  voice,
  user,
  onOpenProfile,
  onOpenAccountSettings,
}: CommandBarProps) {
  /** Índice sob o cursor na fileira de servidores, ou null. */
  const [hovered, setHovered] = useState<number | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  /**
   * Deriva o índice do X do mouse em vez de usar `onMouseEnter` por item.
   *
   * Com um handler por item, mover o cursor rápido pela fileira perde eventos e
   * a onda "gruda" no item errado. Medir a posição contra a geometria da linha é
   * contínuo e não depende de o navegador entregar cada enter/leave.
   */
  const handleMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const row = rowRef.current;
      if (!row) return;
      const rect = row.getBoundingClientRect();
      const slot = rect.width / Math.max(1, guilds.length);
      const index = Math.floor((event.clientX - rect.left) / slot);
      setHovered(Math.max(0, Math.min(guilds.length - 1, index)));
    },
    [guilds.length],
  );

  const scaleFor = (index: number): number =>
    hovered === null ? 1 : 1 + MAX_SCALE * magnification(Math.abs(index - hovered));

  const inCall = voice != null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 flex justify-center px-3"
      /*
       * `--z-dropdown` e não `--z-sticky`: em janela estreita o botão flutuante
       * de abrir a gaveta cai perto da cápsula, e ele está em `--z-dropdown`. No
       * nível de baixo a barra passaria POR BAIXO do botão — e a barra é o
       * elemento permanente da tela, não pode ficar atrás de um controle
       * auxiliar. Fica ABAIXO da cortina da gaveta e do modal, que é o certo:
       * quando um deles está aberto, a barra não deve estar acessível.
       */
      style={{
        /*
         * Soma a área segura do aparelho ao recuo.
         *
         * Sem ela, a cápsula encosta na barra de gestos do Android — onde o
         * sistema captura o toque antes da página. Os botões da borda de baixo
         * ficariam inalcançáveis, e a leitura seria "o app não responde ao
         * toque", não "há uma barra do sistema em cima".
         *
         * `env()` vale zero fora do celular, então a conta dá o recuo de sempre.
         */
        paddingBottom: 'calc(var(--spacing-3) + env(safe-area-inset-bottom, 0px))',
        zIndex: 'var(--z-dropdown)',
      }}
    >
      <style>{`
        /* A magnificação e o encolhimento da cápsula são decoração: quem pediu
           movimento reduzido recebe a barra estática, e nada muda de função. */
        @media (prefers-reduced-motion: reduce) {
          .cb-guild { transition: none !important; transform: none !important; }
          .cb-collapse { transition: none !important; }
        }
        /*
          O medidor do microfone respira no BRILHO, não no tamanho.
          Escalar o botão empurraria os vizinhos a cada sílaba, e a barra
          inteira tremularia — o mesmo motivo pelo qual o anel de "falando" no
          palco é box-shadow e não border.
        */
        .cb-mic-live { transition: box-shadow var(--duration-instant) linear; }

        /* Abaixo de 900px os rótulos saem e sobram os ícones: a cápsula tem de
           caber na largura da janela antes de ter legenda. Abaixo de 620px o
           nome do usuário também sai, e fica só o avatar. */
        @media (max-width: 900px) { .cb-label { display: none } }
        @media (max-width: 620px) { .cb-user-text { display: none } }
      `}</style>

      <div
        /*
         * Sem `overflow` de propósito, e a razão é chata mas decisiva: o
         * tooltip de cada servidor sai pela ARESTA DE CIMA da cápsula e o
         * indicador de estado sai por BAIXO. Qualquer `overflow-x: auto` aqui
         * força `overflow-y` a virar `auto` também (a especificação não permite
         * a combinação visible/auto), e os dois ficariam decapitados.
         *
         * O que absorve janela estreita são os rótulos, que saem por media
         * query, e não uma barra de rolagem.
         */
        className="glass noise pointer-events-auto relative flex items-center"
        style={{
          maxWidth: '100%',
          gap: 'var(--spacing-2)',
          paddingLeft: 'var(--spacing-2)',
          paddingRight: 'var(--spacing-2)',
          paddingTop: 'var(--spacing-2)',
          paddingBottom: 'var(--spacing-2)',
          borderRadius: 'var(--radius-2xl)',
          /*
           * A cápsula é a única superfície de VIDRO persistente da interface, e
           * vale a exceção documentada em base.css: ela flutua SOBRE o
           * conteúdo, então o blur é o que a separa dele. Um painel opaco ali
           * parece uma barra pregada na janela.
           *
           * O brilho de acento é suave: a barra é o mobiliário da tela, e o que
           * precisa brilhar é o que está acontecendo agora — a pílula vermelha
           * e o anel de quem fala, não a moldura.
           */
          boxShadow: 'var(--glass-edge), var(--shadow-capsule), var(--glow-soft)',
        }}
        onMouseLeave={() => setHovered(null)}
      >
        {/* ====================================================================
            ZONA 1 — navegação
        ==================================================================== */}
        {onOpenHome && (
          <GuildButton
            label="Início — amigos e conversas"
            scale={1}
            active={homeActive}
            badge={homeMentions}
            onClick={onOpenHome}
            tone="neutral"
          >
            <HomeIcon />
          </GuildButton>
        )}

        <div ref={rowRef} className="flex items-center" style={{ gap: 'var(--spacing-1)' }}
             onMouseMove={handleMove}>
          {guilds.map((guild, index) => (
            <GuildButton
              key={guild.id}
              label={guild.name}
              scale={scaleFor(index)}
              active={guild.id === activeGuildId}
              unread={guild.unread}
              badge={guild.mentions}
              onClick={() => onSelectGuild(guild.id)}
              tone="accent"
              /*
               * `data-guild-id` é CONTRATO, não sobra de debug. Não remova.
               *
               * O menu de contexto de servidor é capturado por um invólucro em
               * volta da barra, no `AppShell`, que descobre qual servidor foi
               * clicado com `event.target.closest('[data-guild-id]')`. O alvo
               * real do clique é o `<img>` do ícone ou o `<span>` das iniciais —
               * `closest` sobe daí até o botão.
               *
               * Ser atributo e não prop é o que desacopla as duas pontas: esta
               * barra pode ser reescrita inteira sem quebrar o `AppShell`, e o
               * `AppShell` não precisa de nenhuma assinatura daqui.
               */
              guildId={guild.id}
            >
              {/* `framed={false}`: a moldura é do BOTÃO, que já tem o reflexo de
                  aresta e a sombra. Duas molduras concêntricas em 44px desenham
                  um anel duplo visível. */}
              <GuildIcon
                name={guild.name}
                iconUrl={guild.iconUrl ?? null}
                size={GUILD_SIZE}
                shape="circle"
                framed={false}
                fallbackFill={false}
              />
            </GuildButton>
          ))}
        </div>

        <GuildButton label="Criar servidor" scale={1} onClick={onCreateGuild} tone="ghost">
          <PlusIcon />
        </GuildButton>

        {trailing}

        <Divider />

        {/* ====================================================================
            ZONA 2 — a chamada
            ====================================================================
            Envolvida num grid de uma coluna que vai de 0fr a 1fr. É o que
            permite a cápsula ENCOLHER em vez de o bloco piscar: `width: auto`
            não é interpolável, `grid-template-columns` é.

            `visibility` acompanha o colapso porque `overflow: hidden` esconde
            do olho e NÃO do foco — sem isto, Tab pararia num botão de largura
            zero fora da chamada.
        ==================================================================== */}
        <div
          className="cb-collapse grid"
          style={{
            gridTemplateColumns: inCall ? '1fr' : '0fr',
            transitionProperty: 'grid-template-columns, opacity',
            transitionDuration: 'var(--duration-slow)',
            transitionTimingFunction: 'var(--ease-out-expo)',
            opacity: inCall ? 1 : 0,
          }}
        >
          <div
            className="flex items-center overflow-hidden"
            style={{
              gap: 'var(--spacing-1)',
              minWidth: 0,
              visibility: inCall ? 'visible' : 'hidden',
            }}
            aria-hidden={!inCall}
          >
            {voice != null && <CallCluster voice={voice} />}
          </div>
        </div>

        {/* ====================================================================
            ZONA 2b — configurações do aplicativo
            ====================================================================
            Fica DEPOIS da pílula de encerrar e antes do divisor final: é ação de
            aplicação, não de chamada nem de identidade. Ganha rótulo porque
            está na mesma fileira dos controles rotulados, e uma engrenagem sem
            legenda entre quatro botões com legenda lê como um item quebrado.
        ==================================================================== */}
        <ActionButton label="Configurações" onClick={onOpenSettings}>
          <GearIcon />
        </ActionButton>

        {/* ====================================================================
            ZONA 3 — identidade
        ==================================================================== */}
        {user && (
          <>
            <Divider />
            <UserChip
              user={user}
              {...(onOpenProfile ? { onOpenProfile } : {})}
              {...(onOpenAccountSettings ? { onOpenSettings: onOpenAccountSettings } : {})}
            />
          </>
        )}
      </div>
    </div>
  );
}

/* ==============================================================================
   Bloco de controles da chamada
============================================================================== */

function CallCluster({ voice }: { voice: CommandBarVoice }) {
  const atScreenLimit = voice.screenCount >= voice.maxScreens;
  const budget = voice.budgetKbps ?? 0;
  const usage = budget > 0 ? (voice.uploadKbps ?? 0) / budget : 0;

  return (
    <>
      {/* --------------------------------------------------------------------
          Contexto da chamada — nome do canal e quantos estão dentro
          --------------------------------------------------------------------
          Some junto com os controles porque só existe durante a chamada. Fica
          ANTES dos botões: é a resposta à pergunta "onde eu estou", e ela vem
          antes de "o que eu faço".
      -------------------------------------------------------------------- */}
      <div className="cb-label mr-1 flex min-w-0 items-center" style={{ gap: 'var(--spacing-2)' }}>
        <span
          aria-hidden="true"
          className="block size-2 shrink-0 rounded-full"
          style={{
            background: 'var(--color-success)',
            /* Halo maior que o ponto. 8px de sombra num ponto de 8px lê como um
               ponto; 14px lê como algo aceso. */
            boxShadow: '0 0 14px var(--color-success), 0 0 4px var(--color-success)',
          }}
        />
        <div className="min-w-0" style={{ maxWidth: 150 }}>
          <p className="truncate font-medium leading-tight"
             style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}>
            {voice.channelName}
          </p>
          <p className="leading-tight"
             style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}>
            {voice.participantCount === 1
              ? 'só você'
              : `${voice.participantCount} conectados`}
          </p>
        </div>
      </div>

      {/* ====================================================================
          Entrada e saída de áudio, JUNTAS
          ====================================================================
          A ordem anterior era Microfone · Câmera · Compartilhar · Silenciar, e
          separava as duas coisas que formam um par: o que sai da minha boca e o
          que entra no meu ouvido. Ambas são "meu áudio", ambas são apertadas
          no reflexo de "não quero ser ouvido / não quero ouvir", e ficavam nas
          duas pontas do grupo com dois botões de vídeo no meio.

          Agora: [Microfone] [seta de dispositivos] [Fone] │ [Câmera] [Tela].
          Áudio de um lado do fio, vídeo do outro.
      ==================================================================== */}
      <div className="flex items-center" style={{ gap: 'var(--spacing-1)' }}>
        <ActionButton
          label="Microfone"
          srLabel={voice.micEnabled ? 'Desativar microfone' : 'Ativar microfone'}
          pressed={!voice.micEnabled}
          danger={!voice.micEnabled}
          level={voice.micEnabled ? (voice.micLevel ?? 0) : 0}
          onClick={voice.onToggleMic}
        >
          <MicIcon off={!voice.micEnabled} />
        </ActionButton>

        {voice.onOpenDevices && (
          <DeviceChevron
            label="Escolher microfone"
            section="input"
            onOpen={voice.onOpenDevices}
          />
        )}

        <ActionButton
          label="Fone"
          srLabel={voice.deafened ? 'Reativar todo o áudio' : 'Silenciar todo o áudio'}
          pressed={voice.deafened}
          danger={voice.deafened}
          onClick={voice.onToggleDeafen}
        >
          {voice.deafened ? <DeafIcon /> : <HeadphonesIcon />}
        </ActionButton>

        {voice.onOpenDevices && (
          <DeviceChevron
            label="Escolher saída de som"
            section="output"
            onOpen={voice.onOpenDevices}
          />
        )}
      </div>

      {/* Fio entre áudio e vídeo: dois grupos, não seis botões soltos. */}
      <span
        aria-hidden="true"
        className="shrink-0"
        style={{
          width: 1,
          height: 26,
          background: 'var(--color-border-subtle)',
          margin: '0 var(--spacing-1)',
        }}
      />

      <ActionButton
        label="Câmera"
        srLabel={voice.cameraEnabled ? 'Desligar câmera' : 'Ligar câmera'}
        pressed={voice.cameraEnabled}
        active={voice.cameraEnabled}
        onClick={voice.onToggleCamera}
      >
        <CameraIcon off={!voice.cameraEnabled} />
      </ActionButton>

      <ActionButton
        label="Compartilhar Tela"
        srLabel={
          atScreenLimit
            ? `Limite de ${voice.maxScreens} telas atingido`
            : voice.screenCount > 0
              ? `Compartilhar outra tela (${voice.screenCount} ativa${voice.screenCount > 1 ? 's' : ''})`
              : 'Compartilhar tela'
        }
        active={voice.screenCount > 0}
        disabled={atScreenLimit}
        {...(voice.screenCount > 0 ? { badge: voice.screenCount } : {})}
        onClick={voice.onShareScreen}
      >
        <ScreenIcon />
      </ActionButton>

      {/* --------------------------------------------------------------------
          Chip de banda — permanente, não escondido em menu
          --------------------------------------------------------------------
          Em malha P2P o custo de upload cresce com o número de pares
          MULTIPLICADO pelo número de telas: duas telas para sete pessoas são
          quatorze fluxos. Esconder isso faz a chamada degradar sem ninguém
          entender por quê. Ver docs/ARCHITECTURE.md §9.

          Aparece só quando há orçamento medido: um "0.0" fixo seria ruído.
      -------------------------------------------------------------------- */}
      {budget > 0 && (
        <BandwidthChip
          uploadKbps={voice.uploadKbps ?? 0}
          usage={usage}
          {...(voice.notice != null ? { notice: voice.notice } : {})}
          {...(voice.onOpenBandwidth ? { onClick: voice.onOpenBandwidth } : {})}
        />
      )}

      {/* --------------------------------------------------------------------
          Encerrar — pílula vermelha PREENCHIDA, duas linhas
          --------------------------------------------------------------------
          É o único preenchimento saturado da barra, e é intencional que seja
          único: se dois elementos disputam "o mais chamativo", nenhum é. A ação
          irreversível da tela é sair da chamada.

          Duas linhas e não "Sair": o botão fica a 8px do de silenciar, e sair
          da chamada por engano no meio de uma reunião custa a reconexão inteira
          — permissão de microfone, handshake, ICE. O rótulo comprido é fricção
          deliberada; o alvo continua grande.
      -------------------------------------------------------------------- */}
      <LeaveButton onClick={voice.onLeave} />
    </>
  );
}

function LeaveButton({ onClick }: { onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="ml-1 flex shrink-0 items-center transition-all"
      style={{
        gap: 'var(--spacing-2)',
        height: 52,
        paddingLeft: 'var(--spacing-3)',
        paddingRight: 'var(--spacing-4)',
        borderRadius: 'var(--radius-xl)',
        background: hover
          ? 'var(--color-danger-solid-hover)'
          : 'var(--color-danger-solid)',
        color: 'var(--color-on-solid)',
        /* Sombra no matiz do PRÓPRIO botão. Sombra preta sobre fundo escuro é
           invisível, e o botão fica chapado — o oposto do que ele precisa ser. */
        boxShadow: hover
          ? '0 0 0 1px oklch(64% 0.205 25 / 0.55), 0 4px 22px oklch(52% 0.205 25 / 0.60)'
          : '0 0 0 1px oklch(64% 0.205 25 / 0.35), 0 2px 14px oklch(52% 0.205 25 / 0.45)',
        transitionDuration: 'var(--duration-fast)',
      }}
    >
      <span aria-hidden="true" className="grid shrink-0 place-items-center">
        <PhoneDownIcon />
      </span>
      <span className="cb-label text-left leading-tight">
        <span className="block font-semibold" style={{ fontSize: 'var(--text-xs)' }}>
          Encerrar
        </span>
        <span className="block" style={{ fontSize: 'var(--text-2xs)', opacity: 0.82 }}>
          Sair da Chamada
        </span>
      </span>
      <span className="sr-only">Encerrar e sair da chamada</span>
    </button>
  );
}

/* ==============================================================================
   Chip do usuário
============================================================================== */

function UserChip({
  user,
  onOpenProfile,
  onOpenSettings,
}: {
  user: CommandBarUser;
  onOpenProfile?: (() => void) | undefined;
  onOpenSettings?: (() => void) | undefined;
}) {
  const [hover, setHover] = useState(false);
  const status = user.status ?? 'online';
  const presence = user.presenceText ?? STATUS_LABEL[status];

  return (
    <div
      className="flex shrink-0 items-center transition-colors"
      style={{
        gap: 'var(--spacing-2)',
        height: 52,
        paddingLeft: 'var(--spacing-1)',
        paddingRight: 'var(--spacing-1)',
        borderRadius: 'var(--radius-xl)',
        background: hover ? 'oklch(100% 0 0 / 0.05)' : 'transparent',
        transitionDuration: 'var(--duration-fast)',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        type="button"
        onClick={onOpenProfile}
        disabled={!onOpenProfile}
        className="flex min-w-0 items-center px-1"
        style={{
          gap: 'var(--spacing-2)',
          borderRadius: 'var(--radius-lg)',
          cursor: onOpenProfile ? 'pointer' : 'default',
        }}
      >
        <Avatar
          media={user.avatar ?? null}
          name={user.displayName}
          seed={user.id}
          size={32}
          status={status}
        />
        <span className="cb-user-text min-w-0 text-left" style={{ maxWidth: 132 }}>
          <span className="block truncate font-semibold leading-tight"
                style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}>
            {user.displayName}
          </span>
          {/* Ponto + palavra, e não só o ponto: cor sozinha exclui quem não
              distingue matiz, e "estou aparecendo como online?" é a dúvida que
              este chip existe para responder. */}
          {/* Secundário e não terciário: medido, o terciário sobre o fundo de
              hover do chip cai a 4.0:1 e reprova AA — e "estou online?" é
              informação, não decoração. */}
          <span className="flex items-center leading-tight"
                style={{ gap: 4, fontSize: 'var(--text-2xs)',
                         color: 'var(--color-text-secondary)' }}>
            <span aria-hidden="true" className="block size-1.5 shrink-0 rounded-full"
                  style={{
                    background: status === 'online'
                      ? 'var(--color-success)'
                      : status === 'idle'
                        ? 'var(--color-warning)'
                        : status === 'dnd'
                          ? 'var(--color-danger)'
                          : 'var(--color-voice-idle)',
                    boxShadow: status === 'online'
                      ? '0 0 8px var(--color-success)'
                      : 'none',
                  }} />
            <span className="truncate">{presence}</span>
          </span>
        </span>
      </button>

      {onOpenSettings && (
        <button
          type="button"
          onClick={onOpenSettings}
          aria-label="Preferências da conta"
          title="Preferências da conta"
          className="grid shrink-0 place-items-center transition-colors"
          style={{
            width: 30,
            height: 30,
            borderRadius: 'var(--radius-full)',
            color: 'var(--color-text-tertiary)',
            background: hover ? 'oklch(100% 0 0 / 0.06)' : 'transparent',
            transitionDuration: 'var(--duration-fast)',
          }}
        >
          <GearIcon />
        </button>
      )}
    </div>
  );
}

/* ==============================================================================
   Botão de servidor
   ==============================================================================
   Círculo PREENCHIDO de acento, e não um círculo cinza com ícone colorido.

   A diferença não é gosto: com cinco ícones cinzas a fileira lê como uma régua
   de botões desativados, e é o que fazia a tela parecer "sem vida". Preenchido,
   cada servidor é um objeto com presença própria, e o estado ativo tem para onde
   crescer — anel de luz em volta.
============================================================================== */

function GuildButton({
  label,
  scale,
  active = false,
  unread = false,
  badge,
  onClick,
  tone,
  guildId,
  children,
}: {
  label: string;
  scale: number;
  active?: boolean | undefined;
  unread?: boolean | undefined;
  badge?: number | undefined;
  onClick: () => void;
  tone: 'accent' | 'neutral' | 'ghost';
  /** Vira `data-guild-id` no botão. Ver a nota no ponto de uso. */
  guildId?: string | undefined;
  children: ReactNode;
}) {
  const [focusVisible, setFocusVisible] = useState(false);
  const [hover, setHover] = useState(false);
  const magnified = scale > 1.02;
  const showTip = magnified || focusVisible || (hover && scale === 1);

  const background =
    tone === 'accent'
      ? active
        ? 'var(--color-accent-solid-hover)'
        : 'var(--color-accent-solid)'
      : tone === 'neutral'
        ? active
          ? 'var(--color-accent-solid)'
          : 'oklch(100% 0 0 / 0.07)'
        : hover
          ? 'oklch(100% 0 0 / 0.10)'
          : 'oklch(100% 0 0 / 0.04)';

  const color =
    tone === 'accent' || (tone === 'neutral' && active)
      ? 'var(--color-on-solid)'
      : 'var(--color-text-secondary)';

  return (
    <div className="relative flex shrink-0 flex-col items-center">
      {/* Tooltip acima. Aparece na magnificação E no foco por teclado — sem o
          segundo caso, quem navega por Tab não sabe onde está. */}
      <span
        role="tooltip"
        aria-hidden={!showTip}
        className="pointer-events-none absolute whitespace-nowrap px-2 py-1 transition-opacity"
        style={{
          bottom: `calc(100% + var(--spacing-2))`,
          opacity: showTip ? 1 : 0,
          background: 'var(--color-surface-overlay)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--text-2xs)',
          color: 'var(--color-text-primary)',
          boxShadow: 'var(--glass-edge), 0 6px 20px oklch(0% 0 0 / 0.50)',
          transitionDuration: 'var(--duration-fast)',
        }}
      >
        {label}
      </span>

      <button
        type="button"
        onClick={onClick}
        onFocus={() => setFocusVisible(true)}
        onBlur={() => setFocusVisible(false)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-current={active ? 'page' : undefined}
        {...(guildId !== undefined ? { 'data-guild-id': guildId } : {})}
        className="cb-guild relative grid place-items-center overflow-hidden transition-transform"
        style={{
          width: GUILD_SIZE,
          height: GUILD_SIZE,
          /* Cresce para CIMA, apoiado na base da cápsula. Crescer do centro o
             faria afundar na barra. */
          transformOrigin: 'bottom center',
          transform: `scale(${scale})`,
          borderRadius: 'var(--radius-full)',
          background,
          color,
          /*
           * Anel de luz no ativo. `box-shadow` e não `border`: borda muda a
           * caixa e a fileira inteira reflui a cada troca de servidor.
           *
           * O inset claro em cima existe em TODOS os estados: é o mesmo reflexo
           * de aresta dos painéis, aplicado a um objeto redondo. Sem ele o
           * círculo preenchido fica plano como um adesivo.
           */
          boxShadow: active
            ? 'var(--ring-accent), inset 0 1px 0 oklch(100% 0 0 / 0.22)'
            : 'inset 0 1px 0 oklch(100% 0 0 / 0.16), 0 2px 8px oklch(0% 0 0 / 0.35)',
          transitionDuration: 'var(--duration-fast)',
          transitionTimingFunction: 'var(--ease-spring)',
        }}
      >
        {children}
        <span className="sr-only">{label}</span>
      </button>

      {/* Indicador de estado, ABAIXO do ícone e FORA do botão, para a
          magnificação não escalá-lo — um badge que cresce junto vira borrão. */}
      <span className="pointer-events-none absolute flex h-1.5 items-center justify-center"
            style={{ bottom: -7 }}>
        {badge !== undefined && badge > 0 ? (
          <span
            className="flex items-center justify-center px-1 font-bold"
            style={{
              minWidth: 15,
              height: 15,
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-danger-solid)',
              color: 'var(--color-on-solid)',
              fontSize: 9,
              lineHeight: 1,
              boxShadow: '0 0 0 2px oklch(12% 0.035 292 / 0.85)',
            }}
          >
            {badge > 99 ? '99+' : badge}
            <span className="sr-only"> menções</span>
          </span>
        ) : unread ? (
          <>
            <span aria-hidden="true" className="block size-1.5 rounded-full"
                  style={{ background: 'var(--color-text-secondary)' }} />
            <span className="sr-only">não lido</span>
          </>
        ) : active ? (
          <span aria-hidden="true" className="block rounded-full"
                style={{
                  width: 16, height: 3,
                  background: 'var(--color-accent)',
                  boxShadow: '0 0 8px var(--color-accent-glow)',
                }} />
        ) : null}
      </span>
    </div>
  );
}

/* ==============================================================================
   Botão de ação com rótulo — ícone em cima, texto embaixo
============================================================================== */

function ActionButton({
  label,
  srLabel,
  active = false,
  pressed,
  danger = false,
  disabled = false,
  badge,
  level,
  onClick,
  children,
}: {
  label: string;
  srLabel?: string;
  active?: boolean;
  pressed?: boolean;
  danger?: boolean;
  disabled?: boolean;
  badge?: number;
  /** 0 a 1. Modula o brilho, para o botão de microfone. */
  level?: number;
  onClick: () => void;
  children: ReactNode;
}) {
  const [hover, setHover] = useState(false);

  /*
   * O nível do microfone vira INTENSIDADE DE BRILHO, não altura de barra.
   *
   * A pergunta que um app de voz tem de responder sem ser perguntado é "meu
   * microfone está captando?". Um ícone estático não responde; um vúmetro de
   * cinco barrinhas responde e custa 40px de largura na barra mais disputada da
   * tela. O halo responde de relance e não custa largura nenhuma.
   *
   * `level` é elevado a 0.6 porque a percepção de brilho é logarítmica: linear,
   * a fala normal (rms ~0.15) produzia um halo invisível.
   */
  const glow = level !== undefined && level > 0.02
    ? Math.min(1, level ** 0.6)
    : 0;

  const background = danger
    ? 'var(--color-danger-subtle)'
    : active
      ? 'var(--color-accent-subtle)'
      : hover && !disabled
        ? 'oklch(100% 0 0 / 0.08)'
        : 'transparent';

  const color = danger
    ? 'var(--color-danger-on-subtle)'
    : active
      ? 'var(--color-accent-on-subtle)'
      : 'var(--color-text-secondary)';

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={disabled}
      title={srLabel ?? label}
      {...(pressed !== undefined ? { 'aria-pressed': pressed } : {})}
      className="cb-mic-live relative flex shrink-0 flex-col items-center justify-center transition-colors disabled:cursor-not-allowed disabled:opacity-40"
      style={{
        gap: 2,
        minWidth: 58,
        height: 52,
        paddingLeft: 'var(--spacing-2)',
        paddingRight: 'var(--spacing-2)',
        borderRadius: 'var(--radius-lg)',
        background,
        color,
        boxShadow: glow > 0
          ? `0 0 0 1px oklch(76% 0.180 150 / ${(0.25 + glow * 0.45).toFixed(2)}), ` +
            `0 0 ${(6 + glow * 16).toFixed(0)}px oklch(76% 0.180 150 / ${(glow * 0.42).toFixed(2)})`
          : 'none',
        transitionDuration: 'var(--duration-fast)',
      }}
    >
      <span aria-hidden="true" className="grid place-items-center">{children}</span>
      <span className="cb-label whitespace-nowrap font-medium leading-none"
            style={{ fontSize: 'var(--text-2xs)' }}>
        {label}
      </span>
      {srLabel !== undefined && <span className="sr-only">{srLabel}</span>}

      {badge !== undefined && (
        <span
          aria-hidden="true"
          className="absolute grid place-items-center font-bold"
          style={{
            top: 4, right: 4,
            minWidth: 15, height: 15,
            padding: '0 3px',
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-accent-solid)',
            color: 'var(--color-on-solid)',
            fontSize: 9,
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

/* ==============================================================================
   Chip de banda
============================================================================== */

function BandwidthChip({
  uploadKbps,
  usage,
  notice,
  onClick,
}: {
  uploadKbps: number;
  usage: number;
  notice?: string | undefined;
  onClick?: (() => void) | undefined;
}) {
  /*
   * Três faixas, e os limites não são arbitrários.
   *
   * Acima de 85% do orçamento a fila de saída começa a encher e a perda de
   * pacote aparece; acima de 65% já não há folga para uma tela nova. São limites
   * de AVISO, não de corte — cortar é responsabilidade do governador de banda.
   */
  const state = usage >= 0.85 ? 'critical' : usage >= 0.65 ? 'warn' : 'ok';
  const mbps = (uploadKbps / 1000).toFixed(1);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={notice ?? `Upload: ${mbps} Mbps, ${(usage * 100).toFixed(0)}% do orçamento`}
      className="flex shrink-0 flex-col items-center justify-center"
      style={{
        gap: 2,
        height: 52,
        paddingLeft: 'var(--spacing-2)',
        paddingRight: 'var(--spacing-2)',
        borderRadius: 'var(--radius-lg)',
        background: state === 'ok' ? 'transparent' : 'var(--color-warning-subtle)',
        color:
          state === 'critical'
            ? 'var(--color-danger-on-subtle)'
            : state === 'warn'
              ? 'var(--color-warning)'
              : 'var(--color-text-tertiary)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <span className="font-medium tabular-nums"
            style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)' }}>
        {mbps}
      </span>
      <span className="cb-label whitespace-nowrap leading-none"
            style={{ fontSize: 'var(--text-2xs)' }}>
        Mbps
      </span>
      <span className="sr-only">
        {mbps} megabits por segundo de upload, {(usage * 100).toFixed(0)} por cento do
        orçamento{notice !== undefined ? `. ${notice}` : ''}
      </span>
    </button>
  );
}

/* ==============================================================================
   Divisor
   ==============================================================================
   Fio de 1px com GRADIENTE que desvanece nas pontas, e não um retângulo sólido.

   Um fio sólido de ponta a ponta desenha um traço, e traço é o que a direção
   visual deste projeto evita — o "contorno horrível". Desvanecido, ele lê como
   uma dobra na superfície: separa sem cortar.
============================================================================== */
function Divider() {
  return (
    <span
      aria-hidden="true"
      className="shrink-0 self-center"
      style={{
        width: 1,
        height: 32,
        background:
          'linear-gradient(to bottom, transparent, oklch(100% 0 0 / 0.16) 30%, ' +
          'oklch(100% 0 0 / 0.16) 70%, transparent)',
      }}
    />
  );
}

/* ==============================================================================
   Ícones
   ==============================================================================
   Inline e no mesmo traço do resto do repositório (stroke de 1.4-1.7 em caixa de
   16-20). Sem biblioteca: um conjunto de ícones inteiro para vinte glifos é
   peso de bundle e uma segunda gramática visual dentro da mesma tela.
============================================================================== */

function HomeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="7.6" cy="7" r="2.7" stroke="currentColor" strokeWidth="1.6" />
      <path d="M2.6 16.4c0-2.7 2.2-4.4 5-4.4s5 1.7 5 4.4"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M14 5.6a2.5 2.5 0 010 4.8M16.8 16.4c0-1.9-.5-3-1.5-3.8"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M9 4v10M4 9h10" stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="2.6" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 2.4v2M10 15.6v2M2.4 10h2M15.6 10h2M4.6 4.6l1.4 1.4M14 14l1.4 1.4M15.4 4.6L14 6M6 14l-1.4 1.4"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function MicIcon({ off }: { off: boolean }) {
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 2.6a2.4 2.4 0 012.4 2.4v4.4a2.4 2.4 0 01-4.8 0V5A2.4 2.4 0 0110 2.6z"
            stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M5.4 9.2a4.6 4.6 0 009.2 0M10 14.4v2.8"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      {off && (
        <path d="M3.4 3.4l13.2 13.2" stroke="currentColor" strokeWidth="1.7"
              strokeLinecap="round" />
      )}
    </svg>
  );
}

function CameraIcon({ off }: { off: boolean }) {
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2" y="5.4" width="11" height="9.2" rx="2"
            stroke="currentColor" strokeWidth="1.5" />
      <path d="M13 9.4l4.8-2.8v6.8L13 10.6v-1.2z"
            stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      {off && (
        <path d="M2.8 2.8l14.4 14.4" stroke="currentColor" strokeWidth="1.7"
              strokeLinecap="round" />
      )}
    </svg>
  );
}

function ScreenIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2" y="3.6" width="16" height="11" rx="1.8"
            stroke="currentColor" strokeWidth="1.5" />
      <path d="M7.4 17.4h5.2" stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" />
      <path d="M10 6.6v5M8 8.6L10 6.6l2 2" stroke="currentColor"
            strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HeadphonesIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3.4 13V9.6a6.6 6.6 0 1113.2 0V13"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="1.8" y="12" width="3.6" height="5.2" rx="1.6"
            stroke="currentColor" strokeWidth="1.5" />
      <rect x="14.6" y="12" width="3.6" height="5.2" rx="1.6"
            stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function DeafIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3.4 13V9.6a6.6 6.6 0 1113.2 0V13"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="1.8" y="12" width="3.6" height="5.2" rx="1.6"
            stroke="currentColor" strokeWidth="1.5" />
      <rect x="14.6" y="12" width="3.6" height="5.2" rx="1.6"
            stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 3l14 14" stroke="currentColor" strokeWidth="1.7"
            strokeLinecap="round" />
    </svg>
  );
}

function PhoneDownIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M4.2 8.4a12 12 0 0111.6 0"
            stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M3.2 8.2l-.5 2.4a1.2 1.2 0 00.9 1.4l2.2.5a1.2 1.2 0 001.4-.8l.4-1.5M16.8 8.2l.5 2.4a1.2 1.2 0 01-.9 1.4l-2.2.5a1.2 1.2 0 01-1.4-.8l-.4-1.5"
            stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
            strokeLinejoin="round" />
      <path d="M7.4 15.6l5.2-5.2M12.6 15.6L7.4 10.4"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
